import React, { useEffect, useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Pressable,
  Modal,
  ScrollView,
  useWindowDimensions,
} from "react-native";
import { ChevronDown, ChevronUp } from "lucide-react";
import { NormalText, RainbowBorder } from "../../../common/native";
import { getDeployDocSections } from "../../../../docs/deployDocs";
import type { DeployDocSection } from "../../../../docs/deployDocs";
import { ADMIN_TOKENS } from "../../../../styles/adminTokens";
import CopyableCodeBlock from "./CopyableCodeBlock";
import { TDeployWidgetOnboardingStepId } from "./deployWidgetOnboardingContent";

type DeployProps = {
  codeSnippet: string;
  deployMode: "widget" | "iframe";
  deployStatus?: boolean;
  standaloneFormUrl?: string;
  focusSectionId?: DeployDocSection["id"];
  highlightedStepId?: TDeployWidgetOnboardingStepId;
  onBack: () => void;
  hideBackButton?: boolean;
};

const extractClinicSdkKey = (codeSnippet: string): string | undefined => {
  const match =
    codeSnippet.match(/clinicsdkkey=["']([^"']+)["']/i) ||
    codeSnippet.match(/clinicSdkKey=["']([^"']+)["']/i) ||
    codeSnippet.match(/clinicSdkKey=([^\\s>]+)/i);

  if (!match) return undefined;

  const rawValue = match[1]?.trim();
  if (!rawValue || rawValue === "ENTER YOUR SDK KEY") return undefined;

  return rawValue;
};

const DeployTab: React.FC<DeployProps> = ({
  codeSnippet,
  deployMode,
  deployStatus,
  standaloneFormUrl,
  focusSectionId,
  highlightedStepId,
  onBack,
  hideBackButton = false,
}) => {
  const { width: viewportWidth, height: viewportHeight } =
    useWindowDimensions();
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({ "custom-html": true });
  const [selectedBuilderTitle, setSelectedBuilderTitle] = useState<string>(
    "Generic HTML Website",
  );
  const [isBuilderModalOpen, setIsBuilderModalOpen] = useState<boolean>(false);
  const [builderAnchor, setBuilderAnchor] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const builderTriggerRef = React.useRef<any>(null);

  const clinicSdkKey = extractClinicSdkKey(codeSnippet);
  const deployDocs = getDeployDocSections(
    clinicSdkKey,
    codeSnippet,
    standaloneFormUrl,
    deployMode,
    false,
  );
  const builderSections = deployDocs.filter(
    (section) => section.id !== "iframe",
  );

  const handleBuilderSelect = (selectedTitle: string) => {
    if (!selectedTitle) return;
    const selectedSection = builderSections.find(
      (section) => section.title === selectedTitle,
    );
    if (!selectedSection) return;
    setSelectedBuilderTitle(selectedSection.title);
    setExpandedSections({ [selectedSection.id]: true });
    setIsBuilderModalOpen(false);
  };

  const openBuilderSelector = () => {
    if (!builderTriggerRef.current?.measureInWindow) {
      setIsBuilderModalOpen(true);
      return;
    }

    builderTriggerRef.current.measureInWindow(
      (x: number, y: number, width: number, height: number) => {
        setBuilderAnchor({ x, y, width, height });
        setIsBuilderModalOpen(true);
      },
    );
  };
  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  useEffect(() => {
    if (!focusSectionId) return;
    setExpandedSections({ [focusSectionId]: true });
    const sectionTitle = deployDocs.find(
      (section) => section.id === focusSectionId,
    )?.title;
    if (sectionTitle) setSelectedBuilderTitle(sectionTitle);
  }, [focusSectionId]);

  return (
    <View style={styles.pageContent}>
      <View style={styles.websiteInstallSection}>
        <View style={styles.sectionHeader}>
          <NormalText
            text="Install On Your Website"
            fontWeight="700"
            style={styles.sectionTitle}
          />
          <Text style={styles.sectionDescription}>
            {deployMode === "iframe"
              ? "Embed the form directly on a page of your website. New submissions will be sent to your email."
              : "Add the widget to your website so you can convert website visitors into leads. New submissions will be sent to your email."}
          </Text>
        </View>
        <View style={styles.builderChooserContainer}>
          <NormalText
            text="What type of website builder are you using?"
            fontWeight="600"
            style={{ fontSize: 16 }}
          />
          <RainbowBorder
            isActive={highlightedStepId === "deploy-guides-and-help"}
            borderRadius={10}
            style={{
              width: "100%",
              maxWidth: 440,
              alignSelf: "flex-start",
            }}
          >
            <TouchableOpacity
              ref={builderTriggerRef}
              style={styles.builderSelectTrigger}
              onPress={openBuilderSelector}
            >
              <NormalText
                text={selectedBuilderTitle || "Select website builder"}
                style={{ fontSize: 16, color: "#0B0E1E" }}
              />
              <ChevronDown size={16} color="#0B0E1E" />
            </TouchableOpacity>
          </RainbowBorder>
          <View style={styles.deployStatusRow}>
            <NormalText
              text={"Deploy Status: "}
              fontWeight="600"
              style={{ fontSize: 16 }}
            />
            <NormalText
              text={deployStatus ? "Deployed!" : "Not Deployed"}
              fontWeight="600"
              style={{
                color: deployStatus ? "#00B7F4" : "#868B97",
                fontSize: 16,
              }}
            />
          </View>
        </View>
        <Modal
          transparent
          visible={isBuilderModalOpen}
          animationType="fade"
          onRequestClose={() => setIsBuilderModalOpen(false)}
        >
          <View style={styles.popoverRoot}>
            <Pressable
              style={styles.popoverBackdrop}
              onPress={() => setIsBuilderModalOpen(false)}
            />
            {builderAnchor ? (
              <View
                style={[
                  styles.builderPopover,
                  (() => {
                    const desiredWidth = Math.max(
                      280,
                      Math.min(440, builderAnchor.width),
                    );
                    const left = Math.max(
                      12,
                      Math.min(
                        builderAnchor.x,
                        viewportWidth - desiredWidth - 12,
                      ),
                    );
                    const top = builderAnchor.y + builderAnchor.height + 6;
                    const maxHeight = Math.max(180, viewportHeight - top - 12);
                    return {
                      left,
                      top,
                      width: desiredWidth,
                      maxHeight,
                    };
                  })(),
                ]}
              >
                <ScrollView
                  showsVerticalScrollIndicator={true}
                  contentContainerStyle={styles.builderModalOptions}
                >
                  {builderSections.map((section) => {
                    const isSelected = selectedBuilderTitle === section.title;
                    return (
                      <Pressable
                        key={section.id}
                        onPress={() => handleBuilderSelect(section.title)}
                        style={[
                          styles.builderModalOption,
                          isSelected && styles.builderModalOptionSelected,
                        ]}
                      >
                        <NormalText
                          text={section.title}
                          style={{
                            fontSize: 16,
                            color: isSelected ? "#0B0E1E" : "#344054",
                          }}
                        />
                      </Pressable>
                    );
                  })}
                </ScrollView>
              </View>
            ) : null}
          </View>
        </Modal>
        <View style={styles.docsContainer}>
          {deployDocs.map((section) => (
            <View key={section.id} style={styles.docSection}>
              <TouchableOpacity
                style={styles.docHeader}
                onPress={() => toggleSection(section.id)}
              >
                <View style={styles.docHeaderLeft}>
                  {expandedSections[section.id] ? (
                    <ChevronUp size={16} color="#00B7F4" />
                  ) : (
                    <ChevronDown size={16} color="#00B7F4" />
                  )}
                  <NormalText
                    text={section.title}
                    fontWeight="600"
                    style={{ fontSize: 16 }}
                  />
                </View>
                <Text style={styles.collapseIndicator}>
                  {expandedSections[section.id] ? "Collapse" : "Expand"}
                </Text>
              </TouchableOpacity>
              {expandedSections[section.id] ? (
                <View style={styles.docBody}>
                  {section.description ? (
                    <Text style={styles.textParagraphNormal}>
                      {section.description}
                    </Text>
                  ) : null}
                  {section.steps.map((step, index) => (
                    <Text key={step} style={styles.listItem}>
                      {index + 1}. {step}
                    </Text>
                  ))}
                  {section.code ? (
                    <View style={styles.iframeCodeBlock}>
                      <CopyableCodeBlock code={section.code} />
                    </View>
                  ) : null}
                  {section.tips?.length ? (
                    <View style={styles.tipsContainer}>
                      <NormalText
                        text={"Tips"}
                        fontWeight="600"
                        style={{ fontSize: 15 }}
                      />
                      {section.tips.map((tip) => (
                        <Text key={tip} style={styles.listItem}>
                          - {tip}
                        </Text>
                      ))}
                    </View>
                  ) : null}
                </View>
              ) : null}
            </View>
          ))}
        </View>
      </View>
      {!hideBackButton ? (
        <View style={styles.flowNavRow}>
          <TouchableOpacity style={styles.flowNavButton} onPress={onBack}>
            <NormalText
              text={"Back: Pick Form Format"}
              style={{ fontSize: 14 }}
            />
          </TouchableOpacity>
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  pageContent: {
    gap: ADMIN_TOKENS.spacing.xxl,
    marginTop: ADMIN_TOKENS.spacing.md,
  },
  websiteInstallSection: {
    width: "100%",
    maxWidth: ADMIN_TOKENS.size.contentMaxWidth,
    gap: ADMIN_TOKENS.spacing.lg,
  },
  sectionHeader: {
    gap: ADMIN_TOKENS.spacing.sm,
  },
  sectionTitle: {
    fontSize: ADMIN_TOKENS.fontSize.xl,
    lineHeight: ADMIN_TOKENS.lineHeight.xl,
    color: ADMIN_TOKENS.color.textPrimary,
  },
  sectionDescription: {
    fontFamily: ADMIN_TOKENS.font.medium,
    fontSize: ADMIN_TOKENS.fontSize.md,
    lineHeight: ADMIN_TOKENS.lineHeight.md,
    color: ADMIN_TOKENS.color.textSecondary,
  },
  textParagraphNormal: {
    fontFamily: "Urbanist500",
    fontWeight: "500",
    fontSize: 16,
    marginTop: 10,
  },
  link: {
    color: "#00B7F4",
    textDecorationColor: "#fff",
  },
  codeInline: {
    fontFamily: "Urbanist500",
    fontWeight: "500",
    backgroundColor: "rgba(0, 0, 0, 0.06)",
    paddingHorizontal: 4,
    paddingVertical: 1,
    borderRadius: 4,
  },
  docsContainer: {
    gap: 16,
    marginTop: 10,
  },
  builderChooserContainer: {
    gap: 8,
    marginTop: 4,
  },
  deployStatusRow: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 2,
  },
  builderSelectTrigger: {
    width: "100%",
    maxWidth: 440,
    borderWidth: 1,
    borderColor: "#C4C6CD",
    borderRadius: 10,
    minHeight: 46,
    paddingHorizontal: 12,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#FFFFFF",
  },
  builderModalOptions: {
    gap: 8,
  },
  builderModalOption: {
    minHeight: 44,
    borderWidth: 1,
    borderColor: "#E4E7EC",
    borderRadius: 8,
    paddingHorizontal: 12,
    justifyContent: "center",
    backgroundColor: "#FFFFFF",
  },
  builderModalOptionSelected: {
    borderColor: "#00B7F4",
    backgroundColor: "rgba(0, 183, 244, 0.08)",
  },
  popoverRoot: {
    flex: 1,
  },
  popoverBackdrop: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    backgroundColor: "transparent",
  },
  builderPopover: {
    position: "absolute",
    borderWidth: 1,
    borderColor: "#D0D5DD",
    borderRadius: 10,
    backgroundColor: "#FFFFFF",
    padding: 8,
    shadowColor: "#000000",
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.16,
    shadowRadius: 16,
    elevation: 16,
  },
  docSection: {
    gap: 6,
  },
  docHeader: {
    backgroundColor: "rgba(0, 183, 244, 0.08)",
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 12,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  docHeaderLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  collapseIndicator: {
    fontFamily: "Urbanist500",
    fontWeight: "500",
    fontSize: 13,
    color: "#00B7F4",
  },
  docBody: {
    gap: 6,
  },
  listItem: {
    fontFamily: "Urbanist500",
    fontWeight: "500",
    fontSize: 15,
    color: "#0B0E1E",
  },
  iframeCodeBlock: {
    marginTop: 6,
    width: "100%",
    maxWidth: 550,
  },
  tipsContainer: {
    marginTop: 6,
    gap: 4,
  },
  flowNavRow: {
    marginTop: 16,
    alignItems: "flex-start",
  },
  flowNavButton: {
    backgroundColor: "#F2F4F7",
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 999,
  },
});

export default DeployTab;
